"use client";

import Image from "next/image";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { m, useReducedMotion } from "motion/react";
import { useRevealOnce } from "@/lib/hooks/use-reveal-once";
import { useTranslation } from "@/lib/hooks/use-translation";

type Faq = {
  q: string;
  a: string;
};

type FaqGroup = {
  label: string;
  items: Faq[];
};

/** The groups, in comp order. Copy lives in `home.faq.groups`; what stays here
    is the reading order and how many questions each group holds, which is what
    the stagger arithmetic below needs and what a translation cannot change. */
const GROUP_KEYS = ["general", "features", "pricing"] as const;
const GROUP_SIZES: Record<(typeof GROUP_KEYS)[number], number> = {
  general: 2,
  features: 2,
  pricing: 3,
};

/** Where each group's stagger starts: the running count of rows (label
    included) that come before it, so the cascade carries on through the later
    labels instead of restarting at each one. Derived rather than written down,
    so adding a question can't leave two rows sharing a delay. */
const GROUP_OFFSETS = GROUP_KEYS.reduce<number[]>((acc, _key, i) => {
  acc.push(i === 0 ? 0 : acc[i - 1] + 1 + GROUP_SIZES[GROUP_KEYS[i - 1]]);
  return acc;
}, []);

/** One step of the cascade, in seconds. */
const STAGGER = 0.07;

/** The first question is open on arrival — the comp shows it that way, and it
    means the section lands showing what an opened row looks like instead of
    seven identical closed bars. */
const DEFAULT_OPEN = "0-0";

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Faq — v2 section 8, laid out from the comp: a fixed 420x480 portrait on the
 * left, the grouped accordion filling whatever is left, 56px between them.
 *
 * The two columns are top-aligned rather than stretched, so the picture keeps
 * the comp's exact height while the list grows and shrinks underneath it as
 * rows open — a stretched picture would resize on every toggle.
 *
 * The entrance is CSS off `useRevealOnce` like every other section here
 * (`--fq-d` staggers the rows, counted straight through all three groups),
 * which leaves Motion doing only the one thing CSS can't: animating a panel
 * from 0 to its own auto height.
 */
export default function Faq() {
  const { t } = useTranslation();
  const reveal = useRevealOnce<HTMLElement>(0.15);
  const [openKey, setOpenKey] = useState<string | null>(DEFAULT_OPEN);

  return (
    <section
      ref={reveal}
      id="faq"
      aria-label={t("home.faq.aria")}
      // `scroll-mt-13` clears the fixed header for the navbar drawer's link here.
      className="fq-reveal scroll-mt-13 bg-[#FCFCFC]"
    >
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-12 px-4 py-12 md:px-[80px]">
        <header className="flex flex-col items-center gap-2 text-center">
          <h2 className="fq-heading text-[26px]/[38px] font-semibold text-black [text-wrap:balance] sm:text-[32px]/[44px] lg:text-[40px]/[48px]">
            {t("home.faq.heading")}
          </h2>
          <p className="fq-sub text-[17px]/[26px] text-black/60 [text-wrap:balance] sm:text-[20px]/[28px]">
            {t("home.faq.sub")}
          </p>
        </header>

        <div className="grid w-full max-w-[1248px] grid-cols-1 items-start gap-10 lg:grid-cols-[420px_1fr] lg:gap-14">
          {/* Decorative: the questions beside it carry the meaning, so an alt
              string here would only be read out ahead of them.

              `aspect-[420/480]` below `lg` so the frame shrinks with the
              column instead of overflowing a 375px phone; from `lg` it is the
              comp's exact 420x480 box. */}
          <div className="fq-art relative aspect-[420/480] w-full overflow-hidden rounded-[32px] lg:h-[480px] lg:w-[420px]">
            {/* `%20`, not a literal space: `next/image` emits a `srcset`, and
                srcset is a space/comma-delimited list — an unencoded space in
                the URL splits the entry and the browser drops the candidate. */}
            <Image
              src="/v2/Rectangle%2024.webp"
              alt=""
              aria-hidden
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 420px"
            />
          </div>

          <div className="flex w-full flex-col gap-8">
            {GROUP_KEYS.map((groupKey, groupIndex) => {
              const group = t(`home.faq.groups.${groupKey}`, {
                returnObjects: true,
              }) as unknown as FaqGroup;

              return (
              <div key={groupKey} className="flex flex-col gap-2">
                <GroupLabel
                  label={group.label}
                  delay={GROUP_OFFSETS[groupIndex] * STAGGER}
                />

                <div className="flex flex-col gap-2.5">
                  {group.items.map((item, itemIndex) => {
                    const key = `${groupIndex}-${itemIndex}`;

                    return (
                      <Row
                        key={item.q}
                        item={item}
                        panelId={`faq-answer-${key}`}
                        delay={
                          (GROUP_OFFSETS[groupIndex] + 1 + itemIndex) * STAGGER
                        }
                        isOpen={openKey === key}
                        onToggle={() =>
                          setOpenKey(openKey === key ? null : key)
                        }
                      />
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Below lg the list runs to ~1558px on a phone, so the section-wide cascade —
 * every group label and row on one `--fq-d` rail — is finished long before the
 * reader has scrolled past the first group. Each item therefore carries its
 * own observer and rises on arrival; above lg the rule that reads this class
 * is inert and the section's single sweep is what runs.
 */
function GroupLabel({ label, delay }: Readonly<{ label: string; delay: number }>) {
  const ref = useRevealOnce<HTMLHeadingElement>(0.3);

  return (
    <h3
      ref={ref}
      style={{ "--fq-d": `${delay}s` } as React.CSSProperties}
      className="fq-item text-[16px]/[24px] font-medium text-black/50"
    >
      {label}
    </h3>
  );
}

function Row({
  item,
  panelId,
  delay,
  isOpen,
  onToggle,
}: Readonly<{
  item: Faq;
  panelId: string;
  delay: number;
  isOpen: boolean;
  onToggle: () => void;
}>) {
  const reduced = useReducedMotion();
  // See `GroupLabel` — same reason, same class.
  const ref = useRevealOnce<HTMLDivElement>(0.3);

  return (
    <div
      ref={ref}
      style={{ "--fq-d": `${delay}s` } as React.CSSProperties}
      className="fq-item fq-row overflow-hidden rounded-[12px] bg-[#F3F3F3]"
    >
      {/* The comp draws the box's 16px bottom padding once, whether the row is
          open or shut. Open, it belongs to the answer, so the button gives it
          up — otherwise the two stack into 32px under the question. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={`flex w-full items-center justify-between gap-4 px-[21px] pt-4 text-left ${
          isOpen ? "pb-0" : "pb-4"
        }`}
      >
        <span className="text-[16px]/[26px] font-medium text-black sm:text-[18px]/[28px]">
          {item.q}
        </span>
        <span
          className="fq-chevron flex-none"
          data-open={isOpen ? "" : undefined}
        >
          <ChevronDown
            className="size-6 text-[#6D717F]"
            strokeWidth={2.4}
            aria-hidden
          />
        </span>
      </button>

      {/* Always mounted, opened and shut by animating its own height.

          This used to be `<AnimatePresence>{isOpen ? <panel> : null}`, which
          unmounted every shut answer — so the HTML the server sent carried all
          seven questions but only the one answer that starts open. Google's FAQ
          markup requires the Q&A in `StructuredData` to match what the page
          actually shows, and the crawlers behind AI answers (GPTBot, ClaudeBot,
          PerplexityBot) read that HTML without running our JS, so six of the
          seven answers existed for nobody but a hydrated browser.

          Content inside a collapsed accordion is fine by Google as long as it
          is in the DOM, which is exactly what this now is: `height: 0` with
          `overflow-hidden` hides it, `inert` keeps it out of the tab order and
          the accessibility tree, and nothing is hidden from a crawler that a
          reader cannot open for themselves. */}
      <m.div
        id={panelId}
        role="region"
        inert={!isOpen}
        // `false`, so the server's shut rows render at height 0 rather than
        // animating open on hydration — and the one open row paints open.
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: reduced ? 0 : 0.3, ease: EASE }}
        className="overflow-hidden"
      >
        <p className="px-[21px] pt-3 pb-4 text-[15px]/[24px] text-black/70 sm:text-[16px]/[24px]">
          {item.a}
        </p>
      </m.div>
    </div>
  );
}
